import { useState, useRef, useCallback } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import {
  performBulkAction,
  performBulkAllAction,
  fetchReviewQueueEntry,
  BulkActionType,
  BulkAllActionType,
  BulkActionParams,
  BulkActionResult,
  ReviewQueueEntry,
} from 'api/DataReview';

interface UseBulkActionOptions {
  channelKey: string;
  // Active tab the dispatch is coming from. Passed in the bulk-action
  // request body so the BE enqueues the queue row with source_tab
  // populated immediately (the worker would otherwise enrich this ~1s
  // later, leaving the initial "Queued action … from ''" log line
  // empty — fixed by letting the FE supply what it already knows).
  sourceTab?: string;
  onTaskComplete?: () => void;
}

/**
 * Per-call toast overrides for executeBulkAction.
 *
 * Default behavior (all undefined): info toast on dispatch, success toast on
 * completion, warning toast on partial failure. Views with bespoke copy or
 * flows where the user has already been warned (e.g. Unmatched bulk Update's
 * "X will be skipped" confirmation) can override selectively.
 */
export interface BulkToastOptions {
  /** Build the success toast message from the completed result. */
  successMessage?: (result: BulkActionResult) => string;
  /** Skip the "started for N product(s)" info toast at dispatch time. */
  suppressInfoToast?: boolean;
  /** Skip the "Failed on N product(s)" warning toast (e.g., user was already warned). */
  suppressFailureToast?: boolean;
}

/**
 * useBulkAction Hook
 *
 * Encapsulates the async bulk action pattern: submit → poll in background → toast.
 *
 * - isBulkActionLoading: true only during the brief submit phase (validation + dispatch).
 *   The UI is NOT blocked during background polling.
 * - hasPendingTasks: true while any background task is still polling.
 *   Views can use this for subtle indicators (e.g., badge, disabled re-select).
 * - onTaskComplete callback fires when a background task finishes, so the view
 *   can refresh its product list.
 *
 * @param channelKey - The current channel key
 * @param onTaskComplete - Optional callback when a background task finishes
 * @returns Object with loading state and execution function
 */
export function useBulkAction({ channelKey, sourceTab, onTaskComplete }: UseBulkActionOptions) {
  const [isBulkActionLoading, setIsBulkActionLoading] = useState(false);
  const [hasPendingTasks, setHasPendingTasks] = useState(false);
  const pendingCount = useRef(0);

  const updatePendingState = useCallback(() => {
    setHasPendingTasks(pendingCount.current > 0);
  }, []);

  /**
   * Execute a bulk action (update, ignore, restore, etc.)
   *
   * 1. Calls performBulkAction() — fast path: BE enqueues + dispatches and
   *    returns immediately with the queue_uid (no synchronous Mongo
   *    validation, no waiting for task_id).
   * 2. Releases UI immediately after dispatch.
   * 3. Polls GET /queue/<uid> every 5s in the background until the row
   *    reaches a terminal status (Completed / Failed), then fires the
   *    completion toast + onTaskComplete. Replaces the legacy
   *    /job/status/<task_id> polling — the queue row IS the source of
   *    truth for status visibility.
   *
   * @param skus - Array of SKUs to perform action on
   * @param action - The action type to perform
   * @returns Promise<boolean> - True if task was dispatched, false if submit failed
   */
  const executeBulkAction = async (
    skus: string[],
    action: BulkActionType,
    actionData?: Record<string, any>,
    toastOptions?: BulkToastOptions,
  ): Promise<boolean> => {
    setIsBulkActionLoading(true);
    try {
      // Step 1: Submit — fast-return dispatch. BE returns 202 with
      // queue_uid immediately; the celery worker takes over async.
      // source_tab is forwarded so the BE enqueues the queue row with
      // it populated up-front, sparing the "Queued action … from ''"
      // empty-source window during the brief Pending phase. The hook's
      // sourceTab is the default; per-call actionData can override.
      const payload: BulkActionParams = {
        skus,
        action,
        ...(sourceTab ? { source_tab: sourceTab } : {}),
        ...(actionData as Partial<BulkActionParams>),
      };
      const asyncResponse = await performBulkAction(channelKey, payload);

      if (!asyncResponse.queue_uid) {
        toast.error(asyncResponse.message || `Failed to start ${action}`);
        return false;
      }

      // Step 2: Release UI — task dispatched successfully.
      // Toast copy matches ticket 11446 AC: a row-level or bulk dispatch
      // shows "Task added to queue" so the user knows to look in the
      // Review Queue drawer for progress (rather than waiting on a
      // foreground spinner).
      if (!toastOptions?.suppressInfoToast) {
        toast.info('Task added to queue');
      }
      setIsBulkActionLoading(false);

      // Step 3: Poll the queue entry until terminal. Shape-translated
      // into the legacy `BulkActionResult` so the existing toast logic
      // and onTaskComplete callbacks keep working unchanged.
      trackQueueEntry(asyncResponse.queue_uid, action, toastOptions);

      return true;
    } catch (error: any) {
      const message = error?.message || `Failed to perform ${action}`;
      console.error(`Failed to perform ${action}:`, error);
      // Handle 409 Conflict (SKUs locked by another operation)
      if (message.includes('being processed') || message.includes('Please wait')) {
        toast.warning(message);
      } else {
        toast.error(message);
      }
      return false;
    } finally {
      setIsBulkActionLoading(false);
    }
  };

  /**
   * Shared background tracker: poll the queue entry until terminal, then
   * toast + fire onTaskComplete. Used by both the SKU-list bulk actions and
   * the whole-tab bulk-all runs (which override the poll cadence — a 500k
   * run takes hours, so it polls gently with a multi-day safety net).
   */
  const trackQueueEntry = (
    queueUid: string,
    action: string,
    toastOptions?: BulkToastOptions,
    pollOptions?: { intervalMs?: number; timeoutMs?: number },
  ): void => {
    pendingCount.current += 1;
    updatePendingState();

    pollQueueEntryUntilTerminal(channelKey, queueUid, pollOptions)
      .then((result: BulkActionResult) => {
        if (result.done) {
          // Resolution order for the success toast:
          //   1. Caller-supplied successMessage builder (override).
          //   2. Backend-provided result.message — the BE owns the copy
          //      so action-specific wording (e.g. Map Override saying
          //      "moved to Processed") doesn't fork between FE views.
          //   3. Generic fallback when the BE didn't send a message.
          let successMsg: string;
          if (toastOptions?.successMessage) {
            successMsg = toastOptions.successMessage(result);
          } else if (result.message) {
            successMsg = result.message;
          } else {
            successMsg = `Successfully performed ${action} on ${result.success_count} product(s)`;
          }
          toast.success(successMsg);
          if (result.failed_count > 0 && !toastOptions?.suppressFailureToast) {
            toast.warning(`Failed on ${result.failed_count} product(s)`);
          }
        } else {
          toast.error(result.message || `${action} failed`);
        }
        onTaskComplete?.();
      })
      .catch((error: any) => {
        toast.error(error?.message || `${action} task failed`);
        onTaskComplete?.();
      })
      .finally(() => {
        pendingCount.current -= 1;
        updatePendingState();
      });
  };

  /**
   * Execute a whole-tab bulk action (ticket 13765) — "Update All" (SKU
   * Matched) / "Create All" (Unmatched). No SKU list: the backend snapshots
   * the tab and drains every row present at click time. Same dispatch →
   * background-poll → toast flow as executeBulkAction, but polls gently
   * (30s) with a multi-day timeout since large channels run for hours; the
   * Review Queue drawer remains the authoritative live view either way.
   *
   * @returns Promise<boolean> - True if the run was dispatched.
   */
  const executeBulkAllAction = async (
    action: BulkAllActionType,
    toastOptions?: BulkToastOptions,
  ): Promise<boolean> => {
    setIsBulkActionLoading(true);
    try {
      const asyncResponse = await performBulkAllAction(channelKey, {
        action,
        ...(sourceTab ? { source_tab: sourceTab } : {}),
      });

      if (!asyncResponse.queue_uid) {
        toast.error(asyncResponse.message || `Failed to start ${action}`);
        return false;
      }

      if (!toastOptions?.suppressInfoToast) {
        toast.info('Task added to queue');
      }
      setIsBulkActionLoading(false);

      trackQueueEntry(asyncResponse.queue_uid, action, toastOptions, {
        intervalMs: BULK_ALL_POLL_INTERVAL_MS,
        timeoutMs: BULK_ALL_POLL_TIMEOUT_MS,
      });

      return true;
    } catch (error: any) {
      const message = error?.message || `Failed to perform ${action}`;
      console.error(`Failed to perform ${action}:`, error);
      // 409s (matching run / another bulk-all / dispatch window) are
      // expected coordination outcomes, not errors.
      if (
        message.includes('in progress')
        || message.includes('being started')
        || message.includes('Please wait')
      ) {
        toast.warning(message);
      } else {
        toast.error(message);
      }
      return false;
    } finally {
      setIsBulkActionLoading(false);
    }
  };

  return { isBulkActionLoading, hasPendingTasks, executeBulkAction, executeBulkAllAction };
}

// Poll a single queue entry every 5s until it reaches a terminal status,
// then translate the row into the legacy `BulkActionResult` shape the
// hook's existing `.then(result => ...)` consumers expect. The queue row
// IS the source of truth for status — the BE's GET /queue/<uid> endpoint
// returns the same shape the Review Queue drawer reads from, so we don't
// need a separate /job/status endpoint.
const QUEUE_POLL_INTERVAL_MS = 5000;
const QUEUE_POLL_TIMEOUT_MS = 30 * 60 * 1000; // 30 min safety net

// Whole-tab bulk-all runs (13765) process up to 500k rows and legitimately
// take hours — poll gently and keep watching for days. The timeout only
// stops THIS page's background toast watcher; the run itself and the Review
// Queue drawer are unaffected by it.
const BULK_ALL_POLL_INTERVAL_MS = 30 * 1000;
const BULK_ALL_POLL_TIMEOUT_MS = 72 * 60 * 60 * 1000; // 72 h safety net

async function pollQueueEntryUntilTerminal(
  csKey: string,
  queueUid: string,
  pollOptions?: { intervalMs?: number; timeoutMs?: number },
): Promise<BulkActionResult> {
  const intervalMs = pollOptions?.intervalMs ?? QUEUE_POLL_INTERVAL_MS;
  const timeoutMs = pollOptions?.timeoutMs ?? QUEUE_POLL_TIMEOUT_MS;
  const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    let entry: ReviewQueueEntry | null = null;
    try {
      const res = await fetchReviewQueueEntry(csKey, queueUid);
      entry = res?.data || null;
    } catch (e) {
      // Transient fetch error — retry on the next tick. The drawer poll
      // will surface persistent failures via its own error state.
      await sleep(intervalMs);
      continue;
    }
    if (entry) {
      if (entry.status === 'Completed' || entry.status === 'Failed') {
        return queueEntryToBulkResult(entry);
      }
    }
    await sleep(intervalMs);
  }
  // Timed out — surface as a generic failure so the caller's onTaskComplete
  // and toast logic still fires. The queue drawer remains the authoritative
  // record either way.
  return {
    done: false,
    success_count: 0,
    failed_count: 0,
    failed: [],
    processing: false,
    message: 'Polling timed out — check the Review Queue for status',
  };
}

function queueEntryToBulkResult(entry: ReviewQueueEntry): BulkActionResult {
  const total = entry.affected_count?.total ?? 0;
  const done = entry.affected_count?.done ?? 0;
  const completed = entry.status === 'Completed';
  const failedCount = completed ? Math.max(0, total - done) : Math.max(0, total - done);
  // The BE puts the (now-properly-formatted) failure_reason on the row
  // when it fails; reuse it as the toast message in the error branch.
  // For success, the result.message slot is left empty so the hook falls
  // back to its action-aware generic ("Successfully performed X on N…").
  const message = completed ? '' : entry.failure_reason || '';
  return {
    done: completed,
    success_count: done,
    failed_count: failedCount,
    failed: [],
    processing: false,
    message,
  };
}
